<template>
    <div class="normal-container scroll-mt-[150px]" id="faq">
        <div
            v-for="(tab, tabIndex) in tabsWithButtonUrls"
            :key="tab.id"
            class="border-green-550 border-t py-16 first:border-none">
            <h3 :id="tabAnchor(tab.title)" class="scroll-mt-[150px]">
                {{ tab.title }}
            </h3>
            <div class="mt-5 grid gap-x-4 lg:grid-cols-2">
                <div v-for="(faq, faqIndex) in tab.faqs" :key="faq._key" class="my-4 flex flex-col">
                    <span
                        :id="faqAnchor(tab.title, faq.question)"
                        @click="toggle(tabIndex, faqIndex)"
                        :aria-expanded="isOpen(tabIndex, faqIndex)"
                        :aria-controls="'accordion-body-' + tabIndex + '-' + faqIndex"
                        class="border-green-550 font-monument-extended flex w-full cursor-pointer scroll-mt-[150px] items-center justify-between border-b-2 px-4 pb-6 text-sm">
                        {{ faq.question }}
                        <svg
                            class="h-3 w-3 shrink-0 transition-transform duration-200"
                            :class="{ 'rotate-180': isOpen(tabIndex, faqIndex) }"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 10 6"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2">
                            <path d="M1 1l4 4 4-4" />
                        </svg>
                    </span>
                    <div
                        v-show="isOpen(tabIndex, faqIndex)"
                        :id="'accordion-body-' + tabIndex + '-' + faqIndex"
                        aria-labelledby="'accordion-heading-' + tabIndex + '-' + faqIndex"
                        class="overflow-hidden transition-all duration-300 ease-in-out"
                        :class="{
                            'max-h-0': !isOpen(tabIndex, faqIndex),
                            'max-h-[1000px]': isOpen(tabIndex, faqIndex),
                        }">
                        <!-- Updated: Support both string and portable text using SanityContent -->
                        <div class="font-mori px-4 pt-3 text-base">
                            <!-- If answer is still a string (backwards compatibility) -->
                            <p v-if="typeof faq.answer === 'string'">{{ faq.answer }}</p>
                            <!-- If answer is portable text array (new format) -->
                            <div v-else-if="Array.isArray(faq.answer)" class="sanity-content">
                                <SanityContent :blocks="faq.answer" :serializers="serializers" />
                            </div>
                            <!-- Fallback for unexpected formats -->
                            <p v-else>{{ String(faq.answer) }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <Button
                class="mx-auto mt-[68px] flex items-center justify-center"
                v-if="tab.button && tab.button.title"
                :link="tab.button.link"
                :buttonType="tab.button.type"
                :type="tab.button.type">
                {{ tab.button.title }}
            </Button>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch } from 'vue';
    import { portableTextToPlainText } from '~/utils/seo';

    const props = defineProps({
        tabs: {
            type: Array,
            required: true,
        },
        button: {
            type: Object,
            required: false,
        },
    });

    useFaqPageSchema(() =>
        (props.tabs ?? []).flatMap(tab =>
            (tab?.faqs ?? []).map(faq => ({
                question: faq?.question,
                answer: portableTextToPlainText(faq?.answer),
            })),
        ),
    );

    // Serializers for SanityContent (same as Content component)
    const serializers = {
        types: {},
    };

    // URL-safe anchor id: strips accents and punctuation so headings can be deep-linked.
    const slugify = value =>
        String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

    const tabAnchor = title => slugify(title);

    // Questions repeat across tabs, so scope the id by tab to keep it unique on the page.
    const faqAnchor = (tabTitle, question) => `${slugify(tabTitle)}-${slugify(question)}`;

    const getButtonUrls = async tabs => {
        if (!tabs) return [];

        try {
            // Extract button IDs from each tab
            const buttonIds = tabs.flatMap(tab =>
                tab.button && tab.button.internalLink ? [tab.button.internalLink._ref] : [],
            );

            const buttonData = await useGetSeoUrl(buttonIds);

            const getUrl = button => {
                try {
                    if (button.buttonType === 'external') {
                        return button.externalUrl;
                    } else if (button.buttonType === 'internal') {
                        if (!button.internalLink) {
                            return '#';
                        }
                        const buttonId = button.internalLink._ref;
                        const seoItem = buttonData.find(item => item.id === buttonId);
                        return seoItem && seoItem?.seoUrls && seoItem?.seoUrls[0]
                            ? seoItem?.seoUrls[0]?.seoPathInfo?.toLowerCase()
                            : '#';
                    }
                    return '#';
                } catch (error) {
                    console.error('Error in getUrl:', error);
                    return '#';
                }
            };

            // Add the correct URL to each button in the tabs
            return tabs.map(tab => ({
                ...tab,
                button: tab.button ? { ...tab.button, link: getUrl(tab.button) } : null,
            }));
        } catch (error) {
            console.error('Error fetching button URLs:', error);
            return tabs;
        }
    };

    const tabsWithButtonUrls = ref([]);

    watch(
        () => props.tabs,
        async newTabs => {
            if (newTabs) {
                tabsWithButtonUrls.value = await getButtonUrls(newTabs);
            }
        },
        { immediate: true },
    );

    const openIndex = ref({});

    const toggle = (tabIndex, faqIndex) => {
        const key = `${tabIndex}-${faqIndex}`;
        if (openIndex.value[key]) {
            delete openIndex.value[key];
        } else {
            openIndex.value = { ...openIndex.value, [key]: true };
            Object.keys(openIndex.value).forEach(k => {
                if (k.split('-')[0] === tabIndex.toString() && k !== key) {
                    delete openIndex.value[k];
                }
            });
        }
    };

    const isOpen = (tabIndex, faqIndex) => {
        const key = `${tabIndex}-${faqIndex}`;
        return !!openIndex.value[key];
    };
</script>

<style scoped></style>
